import {
  Box,
  NextImage,
  dropDetails,
  useBreakpointValue,
  NextLink,
  Flex,
} from '@dapperlabs/core-fe';
import { useHover } from '@dapperlabs/skeleton-ui';
import { isFuture, parseISO } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';
import { useTranslation } from 'src/lib/translations';
import SoldOutImg from 'src/modules/Layout/assets/png/pack_sold_out.png';
import StarterPackImg from 'src/modules/Layout/assets/png/pack_starter_banner.png';
import { PackPriceLabel } from 'src/modules/PackDetailsPage';
import { TimerText } from 'src/modules/PackDetailsPage/components/RowTexts/TimerText';

import { isOngoing } from '../helpers';
import { CTAButton } from './CTAButton';
import { GuaranteeLabel } from './GuaranteeLabel';
import { PackHeading } from './PackHeading';
interface PackDropItemProps {
  availableSupply: number;
  cardBg: string;
  cardImg: string;
  distributionId: number;
  endTime: string;
  guaranteeLabel: string;
  isActiveDrop?: boolean;
  isStarterPack?: boolean;
  packPrice: number;
  packTitle: string;
  startTime: string;
  tier: string;
  totalPacks: number;
}

const slideUp = {
  closed: { transition: { type: 'tween' }, y: '0rem' },
  open: { transition: { type: 'tween' }, y: '-4rem', zIndex: 2 },
};

const PackDropItem = ({
  tier,
  distributionId,
  packTitle,
  packPrice,
  cardBg,
  guaranteeLabel,
  cardImg,
  totalPacks,
  startTime,
  endTime,
  isActiveDrop = false,
  isStarterPack = false,
  availableSupply,
}: PackDropItemProps) => {
  const { t } = useTranslation();

  const isMdUp = useBreakpointValue({ md: true });
  const [hoverRef, isHovered] = useHover();

  const isCTAVisible = !isActiveDrop && !isStarterPack && isHovered && isMdUp;

  const isDropUpcoming = isFuture(parseISO(startTime));
  const isDropOngoing = isActiveDrop && isOngoing(startTime, endTime);
  const isDropSoldOut = isActiveDrop && availableSupply === 0;

  let CTALabel = 'UpcomingDrops.PackDropButtonText';
  if ((isDropOngoing && !isDropSoldOut) || isStarterPack) {
    CTALabel = 'Drops.BuyNowLabel';
  } else if (isActiveDrop && isDropUpcoming) {
    CTALabel = 'PackDetail.QueueState.BtnLabel.WaitroomOpen';
  }

  return (
    <Box
      ref={hoverRef}
      overflow='hidden'
      width={['100%', '100%', '582px']}
      pb={[6, 7]}
      border='1px solid #393B45'
      borderRadius='20px'
      cursor='pointer'
    >
      <NextLink href={dropDetails(distributionId)} legacyBehavior passHref>
        <Box
          sx={{
            aspectRatio: '3 / 2',
          }}
          position='relative'
          alignItems='center'
          justifyContent='center'
          display='flex'
          width={['100%']}
          backgroundImage={cardBg}
          backgroundSize='100% auto'
          backgroundPosition='top'
          backgroundRepeat='no-repeat'
          borderTopRadius='20px'
        >
          <NextImage
            objectFit='cover'
            alt='pack image'
            layout='fill'
            src={cardImg}
          />

          {isStarterPack && (
            <Box
              position='absolute'
              zIndex={3}
              right={0}
              bottom={0}
              width='157px'
              height='149px'
              bgImage={StarterPackImg.src}
              bgSize='100% 149px'
              bgPosition='center center'
              bgRepeat='no-repeat'
            />
          )}
          {isDropSoldOut && (
            <Box
              position='absolute'
              zIndex={3}
              top={0}
              width='100%'
              height='100%'
              bgImage={SoldOutImg.src}
              bgSize='100% 157px'
              bgPosition='center center'
              bgRepeat='no-repeat'
            />
          )}
          {guaranteeLabel && (
            <Box position='absolute' top={2} left={4}>
              <GuaranteeLabel label={guaranteeLabel} />
            </Box>
          )}
          {isActiveDrop && isDropUpcoming && (
            <Box
              position='absolute'
              right={0}
              bottom={0}
              left={0}
              p={4}
              opacity='80%'
              bgColor='gray.800'
            >
              <TimerText
                drawTime={startTime}
                timerLabel={t('UpcomingDrops.CurrentDropTimerLabel')}
                timerNumberProps={{
                  fontFamily: 'mono',
                  fontWeight: 400,
                }}
                timerUnitProps={{
                  fontFamily: 'mono',
                }}
              />
            </Box>
          )}
        </Box>
      </NextLink>

      <Box
        as={motion.div}
        zIndex={100}
        px={[4, 4, 8]}
        animate={isCTAVisible ? 'open' : 'closed'}
        bgColor='rgba(0,0,0,0.7)'
        variants={slideUp}
      >
        <Flex justifyContent='space-between' flexDirection='column'>
          <NextLink href={dropDetails(distributionId)} legacyBehavior passHref>
            <Box py={[6, 7]}>
              <PackHeading
                availableSupply={availableSupply}
                packTitle={packTitle}
                tier={tier}
                totalPacks={totalPacks}
              />

              <PackPriceLabel priceTag={packPrice} />
            </Box>
          </NextLink>

          <AnimatePresence>
            {isCTAVisible && (
              <motion.div
                key={`view-cta-${distributionId}`}
                transition={{ delay: 0.15 }}
                animate={{
                  height: 'auto',
                  opacity: 1,
                  visibility: 'visible',
                  y: '0%',
                  zIndex: 2,
                }}
                exit={{
                  opacity: 0,
                }}
                initial={{
                  height: 0,
                  opacity: 0,
                  visibility: 'hidden',
                  y: '100%',
                }}
              >
                <Box position='absolute' right={0} left={0}>
                  <CTAButton
                    href={dropDetails(distributionId)}
                    isDropOngoing={isDropOngoing}
                    label={t(CTALabel)}
                  />
                </Box>
              </motion.div>
            )}
          </AnimatePresence>

          {(isActiveDrop || isStarterPack) && (
            <CTAButton
              href={dropDetails(distributionId)}
              isDropOngoing={(isDropOngoing && !isDropSoldOut) || isStarterPack}
              label={t(CTALabel)}
            />
          )}
        </Flex>
      </Box>
    </Box>
  );
};

export { PackDropItem };
